import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountSolution } from '@thingslog/repositories';

type SolutionTypeOptionValue = AccountSolution;

export interface SolutionTypeOption {
  value: SolutionTypeOptionValue;
  label: string;
}

const SOLUTION_TYPE_TRANSLATION_KEYS: Record<AccountSolution, string> = {
  [AccountSolution.NONE]: 'company_manager_solution_option_none',
  [AccountSolution.AGRI]: 'company_manager_solution_option_agri',
  [AccountSolution.AIR_QUALITY]: 'company_manager_solution_option_air_quality',
  [AccountSolution.STREET_LIGHTS]: 'company_manager_solution_option_street_lights',
  [AccountSolution.PUMP_STATION]: 'company_manager_solution_option_pump_station',
  [AccountSolution.SMART_WASTE]: 'company_manager_solution_option_smart_waste',
  [AccountSolution.LEVEL]: 'company_manager_solution_option_level',
  [AccountSolution.GAS]: 'company_manager_solution_option_gas',
  [AccountSolution.PRESSURE]: 'company_manager_solution_option_pressure',
  [AccountSolution.SMART_WATER]: 'company_manager_solution_option_smart_water',
  [AccountSolution.ENERGY]: 'company_manager_solution_option_energy',
  [AccountSolution.ENVIRONMENT_MONITORING]:
    'company_manager_solution_option_environment_monitoring',
};

export const useCompanyUtils = (): {
  getSolutionTypeLabel: (solutionType: AccountSolution | null | undefined) => string | null;
  solutionTypeOptions: SolutionTypeOption[];
} => {
  const { t } = useTranslation();

  const getSolutionTypeLabel = useMemo(
    () =>
      (solutionType: AccountSolution | null | undefined): string | null => {
        if (!solutionType) {
          return null;
        }

        const translationKey = SOLUTION_TYPE_TRANSLATION_KEYS[solutionType];
        return translationKey ? t(translationKey) : null;
      },
    [t]
  );

  const solutionTypeOptions = useMemo<SolutionTypeOption[]>(
    () => [
      ...Object.values(AccountSolution).map((solutionType: AccountSolution) => ({
        value: solutionType,
        label: getSolutionTypeLabel(solutionType) || solutionType,
      })),
    ],
    [getSolutionTypeLabel, t]
  );

  return { getSolutionTypeLabel, solutionTypeOptions };
};

export type { SolutionTypeOptionValue };
